import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { formatCodeByFileType } from '@common/codemirror/util/format';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabDocCommitEvent } from '@common/collab/events/collab-doc-commit.event';
import { CollabDocDeleteEvent } from '@common/collab/events/collab-doc-delete.event';
import { CollabDocDiscardEvent } from '@common/collab/events/collab-doc-discard.event';
import { CollabDocMergeEvent } from '@common/collab/events/collab-doc-merge.event';
import { CollabDocRenameEvent } from '@common/collab/events/collab-doc-rename.event';
import { CollabDocRestoreEvent } from '@common/collab/events/collab-doc-restore.event';
import { CollabUserJSON } from '@common/collab/models/collab-user-json.model';
import { DocTimelineUpdate } from '@common/collab/types/doc-timeline-update.type';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { CollabSessionOptions } from '@common/sockets/collab-session';
import { CollabSessionSyncState } from '@common/sockets/enums/collab-session-sync-state.enum';
import { getProjectFileTypeBySchemaName } from '@common/util/schema-set';
import { byteSize } from '@common/util/utf';
import { CollabCodeEditorComponent } from '@portal-core/code-editor/components/collab-code-editor/collab-code-editor.component';
import { FlareCodeChangesRecreatorTaskWorker } from '@portal-core/code-editor/util/flare-code-changes-recreator.task-worker';
import { FlareDocStepsRecreatorTaskWorker } from '@portal-core/code-editor/util/flare-doc-steps-recreator.task-worker';
import { TopicPropertiesService } from '@portal-core/file-properties/services/topic-properties.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { FlareTextEditorComponent } from '@portal-core/project-files/components/flare-text-editor/flare-text-editor.component';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { StylesPanelEvent } from '@portal-core/project-files/models/styles-panel-event.model';
import { FlareXmlEditorMode } from '@portal-core/project-files/types/flare-xml-editor-mode.type';
import { EditorChangeEvent } from '@portal-core/text-editor/types/editor-change-event.type';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { WorkspaceEditorPermissionsError } from '@portal-core/workspaces/models/workspace-editor-permissions-error.model';
import { WorkspacesService } from '@portal-core/workspaces/services/workspaces.service';
import { map, Observable, Subject, Subscription, switchMap } from 'rxjs';

@Component({
  selector: 'mc-flare-collab-editor',
  templateUrl: './flare-collab-editor.component.html',
  styleUrls: ['./flare-collab-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TopicPropertiesService],
})
@AutoUnsubscribe()
export class FlareCollabEditorComponent implements OnChanges, OnInit {
  @Input() branchName: string;
  @Input() commitId: string;
  @Input() content?: string;
  @Input() fileId: number;
  @Input() filePath: string;
  @Input() projectFileType?: ProjectFileType = ProjectFileType.Flare;
  @Input() fileType: CollabFileType;
  @Input() getTimelineDocVersion?: CollabSessionOptions['getTimelineDocVersion'];
  @Input() licenseUser: LicenseUser;
  @Input() mode?: FlareXmlEditorMode = 'text';
  @Input() projectId: number;
  @Input() projectLanguage: string;
  @Input() readonly?: boolean = false;
  @Input() showConditions?: boolean;
  @Input() showStylesPanel?: boolean;
  @Input() stylesEnabled?: boolean;
  @Input() trackedChanges?: boolean = false;
  @Input() timelineEnabled?: boolean = false;
  @Input() wrapLines?: boolean;

  @InputObservable('projectId') projectId$: Observable<number>;
  @InputObservable('filePath') filePath$: Observable<string>;

  @Output() docCommit: EventEmitter<CollabDocCommitEvent> = new EventEmitter<CollabDocCommitEvent>();
  @Output() docDelete: EventEmitter<CollabDocDeleteEvent> = new EventEmitter<CollabDocDeleteEvent>();
  @Output() docDiscard: EventEmitter<CollabDocDiscardEvent> = new EventEmitter<CollabDocDiscardEvent>();
  @Output() docMerge: EventEmitter<CollabDocMergeEvent> = new EventEmitter<CollabDocMergeEvent>();
  @Output() docRename: EventEmitter<CollabDocRenameEvent> = new EventEmitter<CollabDocRenameEvent>();
  @Output() docRestore: EventEmitter<CollabDocRestoreEvent> = new EventEmitter<CollabDocRestoreEvent>();
  @Output() syncStateChange: EventEmitter<CollabSessionSyncState> = new EventEmitter<CollabSessionSyncState>();
  @Output() load: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() navToFile: EventEmitter<string> = new EventEmitter<string>();
  @Output() showConditionsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showStylesPanelChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() stylesEnabledChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() stylesPanelEvent: EventEmitter<StylesPanelEvent> = new EventEmitter<StylesPanelEvent>();
  @Output() stylesheetLoad: EventEmitter<void> = new EventEmitter<void>();
  @Output() stylesheetLoaded: EventEmitter<Error | undefined> = new EventEmitter<Error | undefined>();
  @Output() timelineUpdate: EventEmitter<DocTimelineUpdate> = new EventEmitter<DocTimelineUpdate>();
  @Output() trackedChangesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() unload: EventEmitter<void> = new EventEmitter<void>();
  @Output() usersUpdate: EventEmitter<CollabUserJSON[]> = new EventEmitter<CollabUserJSON[]>();
  @Output() wrapLinesChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(FlareTextEditorComponent, { static: true }) flareTextEditor: FlareTextEditorComponent;
  @ViewChild(CollabCodeEditorComponent, { static: true }) collabCodeEditor: CollabCodeEditorComponent;

  /** Whether or not the code editor is loaded. Used for hiding the collab-doc-bar content until the data is ready. */
  codeEditorLoaded: boolean = false;
  /** The code changes recreator for syncing the collab session doc to the code editor. */
  flareCodeChangesRecreatorTaskWorker: FlareCodeChangesRecreatorTaskWorker = new FlareCodeChangesRecreatorTaskWorker();
  /** The doc steps recreator for syncing the code editor to the collab session. */
  flareDocStepsRecreatorTaskWorker: FlareDocStepsRecreatorTaskWorker = new FlareDocStepsRecreatorTaskWorker();
  /** Whether or not the text editor is loaded. Used for hiding the collab-doc-bar content until the data is ready. */
  textEditorLoaded: boolean = false;
  /** If this is set the user doesn't have access so set the collab editor to be readonly. */
  accessError: WorkspaceEditorPermissionsError;
  accessErrorMessage: string;
  accessErrorSubscription: Subscription
  /** Whether or not changes to the document are currently blocked by collab session. */
  blockChanges: boolean = false;

  /** Size of the formatted document. */
  docSize$!: Observable<number>;
  private docSizeChange = new Subject<void>();

  constructor(
    private workspacesService: WorkspacesService,
    private cdr: ChangeDetectorRef,
    private topicPropertiesService: TopicPropertiesService
  ) {
    // (re)calculate size only when a Properties tab is open because it is time consuming
    this.docSize$ = this.docSizeChange.pipe(
      map(() => this.calculateFormattedDocSize())
    );
  }
  /** The collab doc id in the collab system. */
  public get docId(): string {
    return this.flareTextEditor?.docId;
  }

  /** The doc version the doc is at. */
  public get docVersion(): number {
    return this.mode !== 'code' ? this.flareTextEditor?.docVersion : this.collabCodeEditor?.docVersion;
  }

  ngOnInit() {
    // do not allow to edit the file content if the current user does not have enough permissions
    this.accessErrorSubscription = this.projectId$.pipe(
      switchMap((projectId) =>
        this.workspacesService.getAccessErrorMessages$(projectId)
      )
    ).subscribe(accessError => {
      this.accessError = accessError;
      this.setAccessPermissionErrorMessage();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // If the mode changed to the content tab then reflow the toolbar layout to account for viewport resizing while on the code tab
    if (changes.mode) {
      if (this.mode === 'text') {
        // Wait for the next tick to reflow the layout to ensure the text editor is showing
        setTimeout(() => {
          this.flareTextEditor.reflowToolbarLayout();
          this.flareTextEditor.reflowGutterLayout();
        });
      } else if (this.mode === 'props') {
        // Defer emission to the next tick to ensure a child component is fully ready (and subscribed to the docSize$) before emitting a new value.
        setTimeout(() => this.docSizeChange.next());
      }
      this.setAccessPermissionErrorMessage();
    }
  }

  onCodeEditorContentChanged() {
    // If the code editor content has changed then we know its been loaded. Really only the first change is needed to trigger the loaded state.
    this.codeEditorLoaded = true;
  }

  onCodeEditorUnloaded() {
    this.codeEditorLoaded = false;
  }

  onTextEditorLoaded(success: boolean) {
    if (success) {
      this.textEditorLoaded = true;
      this.initializeTopicProperties();
      this.docSizeChange.next();
    }
    this.load.emit(success);
  }

  onTextEditorUnloaded() {
    this.textEditorLoaded = false;
    this.topicPropertiesService.reset();
    this.unload.emit()
  }

  /** Try to update the topic properties on every text editor change. */
  onTextEditorContentChanged(event: EditorChangeEvent) {
    this.topicPropertiesService.update(event.newEditorState);
    this.docSizeChange.next();
  }

  onShowConditionsChanged(showConditions: boolean) {
    this.showConditions = showConditions;
    this.showConditionsChange.emit(showConditions);
  }

  onShowStylesPanelChanged(showStylesPanel: boolean) {
    this.showStylesPanel = showStylesPanel;
    this.showStylesPanelChange.emit(showStylesPanel);
  }

  onStylesPanelEvent(event: StylesPanelEvent) {
    this.stylesPanelEvent.emit(event);
  }

  onStyleSelected(styleName: string) {
    this.flareTextEditor?.onStyleSelected(styleName);
  }

  onStylesEnabledChanged(stylesEnabled: boolean) {
    this.stylesEnabled = stylesEnabled;
    this.stylesEnabledChange.emit(stylesEnabled);
  }

  onTrackedChangesChanged(trackedChanges: boolean) {
    this.trackedChanges = trackedChanges;
    this.trackedChangesChange.emit(trackedChanges);
  }

  onWrapLinesChanged(wrapLines: boolean) {
    this.wrapLines = wrapLines;
    this.wrapLinesChange.emit(wrapLines);
  }

  /** Notifies child components that the block changes state has changed. */
  onBlockChanges(blockChanges: boolean) {
    this.blockChanges = blockChanges;
  }

  public reload() {
    this.flareTextEditor.reload();
    this.collabCodeEditor.reload();
  }

  /** Sets the accessErrorMessage if there is one for the current mode. */
  private setAccessPermissionErrorMessage() {
    this.accessErrorMessage = this.mode !== 'code' ? this.accessError?.contentEditorPermissionsError : this.accessError?.codeEditorPermissionsError;
    this.cdr.markForCheck();
  }

  /** Initializes the topic properties service with the current file path and
   * a transaction dispatcher that applies transactions directly to the text editor. */
  private initializeTopicProperties() {
    const state = this.flareTextEditor?.editorState;
    this.topicPropertiesService.dispatchTransaction = tr => {
      // use text editor view to apply the transaction
      // to ensure the editor state is properly updated and the change is reflected in the editor
      const view = this.flareTextEditor?.editorView;
      if (!view) return false;
      view.dispatch(tr);
      return true;
    };
    this.topicPropertiesService.initialize(state, this.filePath);
  }

  private calculateFormattedDocSize(): number {
    const editorState = this.flareTextEditor?.editorState;
    if (editorState) {
      const schema = editorState.schema as CollabSchema;
      let code = schema.nodeToCode(editorState.doc);
      const projectFileType = getProjectFileTypeBySchemaName(schema.name);
      if (projectFileType) {
        code = formatCodeByFileType(code, projectFileType);
      }
      return byteSize(code);
    }
    return 0;
  }

}
